"use client";

import Image from "next/image";
import { useRef, type ReactNode } from "react";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { reduceMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";

type ImageRevealProps = {
  /** Photo to reveal. Omit and pass `children` to reveal any composed visual instead. */
  src?: string;
  alt?: string;
  children?: ReactNode;
  className?: string;
  innerClassName?: string;
  /** Horizontal travel on entry (e.g. 80 to slide in from the right). */
  fromX?: number;
  delay?: number;
  start?: string;
  /** Scroll-linked drift of the inner layer, as a fraction of its height. */
  parallax?: number;
  radius?: number;
  sizes?: string;
};

/**
 * Editorial image reveal: the frame wipes open (clip-path inset) while the picture inside
 * settles from a slight over-scale — the "camera" pulls back as the frame opens.
 */
export function ImageReveal({
  src,
  alt = "",
  children,
  className,
  innerClassName,
  fromX = 0,
  delay = 0,
  start = "top 80%",
  parallax = 0.05,
  radius = 28,
  sizes = "(min-width: 1024px) 480px, 100vw",
}: ImageRevealProps) {
  const frame = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const el = frame.current;
      const layer = inner.current;
      if (!el || !layer) return;

      if (reduceMotion()) {
        gsap.set(el, { opacity: 1 });
        return;
      }

      gsap.set(el, { clipPath: `inset(var(--clip-top) 0% 0% 0% round ${radius}px)` });
      gsap
        .timeline({ delay, scrollTrigger: { trigger: el, start, toggleActions: PLAY_ONCE } })
        .fromTo(
          el,
          { opacity: 0, x: fromX, scale: 0.96, "--clip-top": "14%" },
          {
            opacity: 1,
            x: 0,
            scale: 1,
            "--clip-top": "0%",
            duration: 1.5,
            ease: "silk",
            clearProps: "clipPath",
          },
        )
        .fromTo(layer, { scale: 1.1 }, { scale: 1, duration: 1.9, ease: "silk" }, 0);

      if (parallax) {
        gsap.fromTo(
          layer,
          { yPercent: -parallax * 100 },
          {
            yPercent: parallax * 100,
            ease: "none",
            scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: true },
          },
        );
      }
    },
    { scope: frame },
  );

  return (
    <div
      ref={frame}
      data-reveal-init=""
      className={cn("relative isolate overflow-hidden", className)}
      style={{ borderRadius: radius }}
    >
      <div ref={inner} className={cn("absolute inset-x-0 -inset-y-[6%]", innerClassName)}>
        {src ? <Image src={src} alt={alt} fill sizes={sizes} className="object-cover" /> : children}
      </div>
    </div>
  );
}
